import React, { useState } from "react";
import { kval, bookAmount, toCurrency, bookClass } from "../BetTableCard32/card32Utils";
import Collapse from "../../../components/Collapse";

const bySr = (a, b) => (a?.sr ?? 0) - (b?.sr ?? 0);

/**
 * 32 Cards B (card32eu) bet table — reference desktop markup (.casino-table.cards32b, isTablet template):
 *  1. .teen1daycasino-container.d-none-small: left = Back/Lay winner markets (main), right = Odd/Even per player (fancy 0..7)
 *  2. .teen1daycasino-container.mt-4.d-none-small: left = Back/Lay colour markets (fancy1), right.right1 = 8 & 9 / 10 & 11 Total (fancy 18, 19)
 *  3. .casino-cards-odds-title (single-card odds = fancy[8].b) + .cards32bextra: Single 1..9, 0 (fancy 8..17)
 * main = sub subtype "card32eu", fancy = "card32eufancy", fancy1 = "card32eufancy1" (each ordered by sr).
 * Book spans next to an odds box give a suspended box the reference's "lock-top" class (v-casinofancybook).
 */
export default function BetTableCard32B({ data = [], onBetClick, exposures = {} }) {
  const [openRanges, setOpenRanges] = useState({});
  const toggleRange = (id) => setOpenRanges((prev) => ({ ...prev, [id]: !prev[id] }));

  const main = data.filter((s) => s?.subtype === "card32eu").sort(bySr);
  const fancy = data.filter((s) => s?.subtype === "card32eufancy").sort(bySr);
  const fancy1 = data.filter((s) => s?.subtype === "card32eufancy1").sort(bySr);
  if (main.length < 4 || fancy.length < 20) return null;

  const click = (m, type) => {
    const v = type === "lay" ? m?.l : m?.b;
    if (m?.gstatus === "OPEN" && v && onBetClick) onBetClick(v, m.nat, type, m);
  };
  const book = (m) => bookAmount(exposures, m?.nat);
  const suspendedCls = (m) => (m?.gstatus !== "OPEN" ? " suspended" : "");
  const lockCls = (m) => (m?.gstatus !== "OPEN" ? " suspended lock-top" : "");

  const range = (id, m, label = "R:") => (
    <>
      <i className="fas fa-info-circle" onClick={() => toggleRange(id)} />{" "}
      <Collapse id={id} open={!!openRanges[id]} className="icon-range">
        {label}
        <span>{kval(m?.min)}</span>-<span>{kval(m?.max)}</span>
      </Collapse>
    </>
  );

  const title = (cols) => (
    <div className="casino-box-row mb-0">
      <div className="casino-nation-name no-border casino-bl-box-title">
        <div className="playerb" />
      </div>{" "}
      <div className="casino-bl-box casino-bl-box-title">
        {cols.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && " "}
            <div className="casino-bl-box-item">
              <b>{c}</b>
            </div>
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  // Name cell with the book amount before the (i) range
  const nameWithBook = (name, m, id) => {
    const amt = book(m);
    return (
      <div className="casino-nation-name">
        <b>{name}</b>{" "}
        <div className="float-right">
          <span className={`mr-2 casino-book ${bookClass(amt)}`}>{toCurrency(amt)}</span>{" "}
          {range(id, m)}
        </div>
      </div>
    );
  };

  const backLayRow = (m, i, idPrefix, name) => (
    <div key={`${idPrefix}${i}`} className="casino-box-row">
      {nameWithBook(name ?? m.nat, m, `${idPrefix}${i}`)}{" "}
      <div className="casino-bl-box">
        <div className={`back casino-bl-box-item${suspendedCls(m)}`} onClick={() => click(m, "back")}>
          <span className="casino-box-odd">{m.b}</span>
        </div>{" "}
        <div className={`lay casino-bl-box-item${suspendedCls(m)}`} onClick={() => click(m, "lay")}>
          <span className="casino-box-odd">{m.l}</span>
        </div>
      </div>
    </div>
  );

  // Odd / Even cell: odds + its own book (v-casinofancybook)
  const oddEvenItem = (m) => {
    const amt = book(m);
    return (
      <div className={`back casino-bl-box-item${lockCls(m)}`} onClick={() => click(m, "back")}>
        <span className="casino-box-odd">{m.b}</span>{" "}
        <span className={`casino-book ${bookClass(amt)}`}>{toCurrency(amt)}</span>
      </div>
    );
  };

  return (
    <>
      <div className="teen1daycasino-container d-none-small">
        <div className="teen1dayleft">
          {title(["Back", "Lay"])}{" "}
          {main.map((m, i) => backLayRow(m, i, "demo-winner-"))}
        </div>{" "}
        <div className="teen1daycenter" />{" "}
        <div className="teen1dayright">
          {title(["Odd", "Even"])}{" "}
          {[0, 1, 2, 3].map((p) => (
            <React.Fragment key={p}>
              {p > 0 && " "}
              <div className="casino-box-row">
                <div className="casino-nation-name">
                  <b>{`Player ${p + 8}`}</b>{" "}
                  <div className="float-right">{range(`demo-odd-evan-${p + 1}`, fancy[p * 2])}</div>
                </div>{" "}
                <div className="casino-bl-box">
                  {oddEvenItem(fancy[p * 2])}{" "}
                  {oddEvenItem(fancy[p * 2 + 1])}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>{" "}
      <div className="teen1daycasino-container mt-4 d-none-small">
        <div className="teen1dayleft">
          {title(["Back", "Lay"])}{" "}
          {fancy1.map((m, i) => backLayRow(m, i, "demo-fancy1-", String(m.nat || "").replace("Three", "3")))}
        </div>{" "}
        <div className="teen1daycenter" />{" "}
        <div className="teen1dayright right1">
          {title(["Back"])}{" "}
          {[18, 19].map((k) => (
            <React.Fragment key={k}>
              {k > 18 && " "}
              <div className="casino-box-row">
                {nameWithBook(fancy[k].nat, fancy[k], `demo-fancy-${k}`)}{" "}
                <div className="casino-bl-box">
                  <div className={`back casino-bl-box-item${suspendedCls(fancy[k])}`} onClick={() => click(fancy[k], "back")}>
                    <span className="casino-box-odd">{fancy[k].b}</span>
                  </div>
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>{" "}
      <div className="text-center mt-4 d-none-small pr casino-cards-odds-title">
        <b>{fancy[8].b}</b>{" "}
        <div className="d-inline-block ml-1 float-right">{range("demo-single", fancy[8])}</div>
      </div>{" "}
      <div className="d-none-small cards32bextra mt-1">
        <div className="casino-bl-box">
          {Array.from({ length: 10 }, (_, i) => {
            const m = fancy[i + 8];
            const amt = book(m);
            return (
              <div key={i} className={`casino-bl-box-item back${lockCls(m)}`} onClick={() => click(m, "back")}>
                <span className="casino-box-odd">{i === 9 ? 0 : i + 1}</span>{" "}
                <div className={`casino-book ${bookClass(amt)}`}>{toCurrency(amt)}</div>
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
}
